import { Linking } from 'react-native'
import Config from 'APP/Config'
import Analytics from 'APP/Services/Analytics'
import I18n from 'APP/I18n'
import { getBrandConfig } from 'APP/Lib/ConfigHelpers'
import type { SupportedLocales } from 'APP/Models/Locale'
import Alert from 'APP/Converse/Alert'
import { logger } from 'APP/Lib/Datadog'

const DIA_BRAND = 'dia'

// Temporary during the Sun Life v2 migration: send members to the new app
const EXTERNAL_BRAND_OVERRIDES: Record<string, string> = { sunlife: 'sunlife_v2' }

// Brand map entry for linking out to another brand's app
export const getExternalBrandConfig = (brandId: string, locale?: SupportedLocales) =>
  getBrandConfig(EXTERNAL_BRAND_OVERRIDES[brandId] ?? brandId, locale)

// Undefined before flags load, and LD/overrides don't enforce the type, so treat a non-array as an empty list
export const isCrossBrandListed = (brand: string, crossBrandBrands?: string[]): boolean =>
  Array.isArray(crossBrandBrands) && crossBrandBrands.includes(brand)

// Users can be enrolled in the current brand, or in a partner brand when both brands are in
// the cross-brand list. A missing brand means the current brand.
export const canEnrolInBrand = (
  targetBrandId: string | null | undefined,
  crossBrandBrands?: string[]
): boolean => {
  if (!targetBrandId) return true
  const currentBrandId = Config.BRAND_ID

  if (targetBrandId === currentBrandId) return true

  const bothBrandsAllowCrossBrand =
    isCrossBrandListed(currentBrandId, crossBrandBrands) &&
    isCrossBrandListed(targetBrandId, crossBrandBrands)
  return bothBrandsAllowCrossBrand
}

export const getDisplayableBrands = (
  eligibleBrands: string[] | null | undefined,
  crossBrandBrands?: string[]
): string[] | null | undefined => {
  const currentBrand = Config.BRAND_ID
  const isListed = (brand: string) => isCrossBrandListed(brand, crossBrandBrands)

  const canDisplay = (brand: string): boolean => {
    if (brand === currentBrand) return false
    // dia shows every other brand, and every brand shows dia,
    // regardless of the cross-brand list
    if (currentBrand === DIA_BRAND || brand === DIA_BRAND) return true
    // Partner brands only show each other when both are in the cross-brand list
    return isListed(currentBrand) && isListed(brand)
  }

  return eligibleBrands && eligibleBrands.filter(canDisplay)
}

const showCannotOpenUrlAlert = (): void => {
  Alert.alert(I18n.t('CannotOpenUrl.title'), I18n.t('CannotOpenUrl.body') ?? '', [
    { text: I18n.t('CannotOpenUrl.ok') ?? '' },
  ])
}

export const openExternalBrandApp = (appName: string | undefined, brandId: string): void => {
  Analytics.trackEvent('button_click', {
    button_text: appName,
    button_value: 'External app launchpad',
  })

  const { url } = getExternalBrandConfig(brandId)

  // A missing URL (e.g. no brand-map entry for this brand) must not reach
  // Linking.canOpenURL, which rejects when handed an undefined value and would
  // otherwise surface as an unhandled rejection and crash the app.
  if (!url) {
    logger.error('Missing launchpad URL for brand')
    showCannotOpenUrlAlert()
    return
  }

  const tryOpen = async () => {
    const canOpen = await Linking.canOpenURL(url)
    if (canOpen) {
      await Linking.openURL(url)
    } else {
      showCannotOpenUrlAlert()
    }
  }

  tryOpen().catch((error) => {
    logger.error('Unable to open launchpad URL', { url }, error)
    showCannotOpenUrlAlert()
  })
}
